/**
 * Sysadmin Administrators — parent layout (Wave 3 §10.2 #5 / spec §3.9).
 *
 * The Admins list itself already lives at /sysadmin/admins (built in
 * W1a-22). The new IA wraps it as the v1 sub-tab; physical move can
 * happen later without breaking links via a redirect.
 */

import { createFileRoute, Link, Outlet, useLocation } from '@tanstack/react-router';
import { ShieldCheck, MonitorSmartphone, History } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/sysadmin/administrators')({
  component: AdministratorsLayout,
});

const SUB_TABS: Array<{
  key: string; label: string; icon: typeof ShieldCheck; to: string;
  status: 'v1' | 'v2' | 'future';
  external?: boolean;
}> = [
  { key: 'admins',   label: 'Admins',   icon: ShieldCheck,        to: '/sysadmin/admins',                   status: 'v1', external: true },
  { key: 'sessions', label: 'Sessions', icon: MonitorSmartphone, to: '/sysadmin/administrators/sessions',  status: 'v2' },
  { key: 'activity', label: 'Activity', icon: History,            to: '/sysadmin/administrators/activity',  status: 'v2' },
];

function AdministratorsLayout() {
  const location = useLocation();

  return (
    <div className="flex flex-col h-full">
      <div className="border-b sticky top-0 bg-background z-10">
        <nav className="flex items-center gap-0.5 overflow-x-auto px-6">
          {SUB_TABS.map((t) => {
            const Icon = t.icon;
            const active = !t.external && (
              location.pathname === t.to ||
              location.pathname.startsWith(t.to + '/')
            );
            return (
              <Link
                key={t.key}
                to={t.to}
                className={cn(
                  'flex items-center gap-2 px-3 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap',
                  active
                    ? 'border-primary text-foreground'
                    : 'border-transparent text-muted-foreground hover:text-foreground',
                )}
              >
                <Icon className="h-4 w-4" />
                {t.label}
                {t.status !== 'v1' && (
                  <Badge variant="secondary" className="ml-1 text-[10px]">
                    {t.status}
                  </Badge>
                )}
              </Link>
            );
          })}
        </nav>
      </div>

      <div className="flex-1 overflow-y-auto">
        <Outlet />
      </div>
    </div>
  );
}
